import Cookies from "js-cookie";
import {routes, useGetDataList, useSearch} from "../routes/api";
import React, {useContext, useEffect, useLayoutEffect, useState} from "react";
import {EnumContext} from "../components/enumContext";
import {useLocation, useNavigate} from "react-router-dom";
import {OrganizationHeaders} from "../util/getFields";
import {organizationFields} from "../util/modelFields";
import {PAGE_SIZE, updateData} from "../util/util";
import {LoadingComponent, NoDataComponent} from "../util/loading";
import {Grid, MenuItem, Select} from "@mui/material";
import NavigateBreadcrumbs from "../components/navBreadcrumbs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchBox from "../components/searchBox";
import {DataGrid} from "@mui/x-data-grid";
import CustomToolbar from "../components/gridToolbar";
import {organizationSearchFields} from "../util/searchFields";


export default function OrganizationList() {
    const token = Cookies.get('token');
    const route = routes.organization;

    const webAdmin = Cookies.get('webAdmin') === 'T';
    const {enums} = useContext(EnumContext);

    const [noData, setNoData] = useState(false);
    const [newData, setNewData] = useState([]);
    const [viewHeaders, setViewHeaders] = useState(
        OrganizationHeaders.filter(key => key !== 'id').map((key) =>
            ({
                field: key,
                headerName: organizationFields.fields[key].display,
                type: organizationFields.fields[key].type === 'boolean' ? 'boolean' : 'string',
                minWidth: 100,
                flex: 1,
                hide: key === 'id',
                hideable: key !== 'o_alias',
                valueGetter: (value) => (Array.isArray(value) ?
                        value.reduce((acc, item) => (acc ? `${acc}, ${item}` : item), '') :
                        value
                )
            })));

    const {data, loading, statusCode: status, handleCall} = useGetDataList(token, route);

    const {
        data: searchData,
        loading: searchLoading,
        statusCode: searchStatus,
        handleCall: searchCall
    } = useSearch(token, route);

    const [totalPages, setTotalPages] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from ? location.state.from : {pathname: `/`};

    const [searchParams, setSearchParams] = useState({});
    const [searching, setSearching] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        o_billing: false,
    });
    const [filteredData, setFilteredData] = useState([]);

    const [paginationModel, setPaginationModel] = useState({
        page: -1,
        pageSize: PAGE_SIZE,
    });

    useLayoutEffect(() => {
        if (location?.state?.from?.pathname?.split('/')[1] === 'organization' || !location?.state?.from?.pathname) {
            setPaginationModel({...paginationModel, page: Number(sessionStorage.getItem('organizationPage'))});
            setSearchParams(JSON.parse(sessionStorage.getItem('organizationSearchParams')) || {});
            setSearching(sessionStorage.getItem('organizationSearching') === 'T');
            setOpenSearch(sessionStorage.getItem('organizationOpenSearch') === 'T');
            setColumnVisibilityModel(JSON.parse(sessionStorage.getItem('organizationColumns')) || {
                o_billing: false,
            });
        } else {
            setPaginationModel({...paginationModel, page: 0});
            sessionStorage.setItem('organizationPage', '0');
            sessionStorage.setItem('organizationSearchParams', '{}');
            sessionStorage.setItem('organizationSearching', 'F');
            sessionStorage.setItem('organizationOpenSearch', 'F');
            sessionStorage.setItem('organizationColumns', JSON.stringify({
                o_billing: false,
            }));
        }
    }, []);

    const handleReset = () => {
        setColumnVisibilityModel({
            o_billing: false,
        });
        sessionStorage.setItem('organizationColumns', JSON.stringify({
            o_billing: false,
        }));
    };

    const fetchData = async () => {
        try {
            if (searching) {
                await searchCall({params: {...searchParams, page: paginationModel.page + 1}});
            } else {
                await handleCall({params: {page: paginationModel.page + 1}});
            }
        } catch (error) {
            console.error('Error fetching organization data:', error);
        }
    }

    useLayoutEffect(() => {
        if (!webAdmin) {
            navigate(from);
        }
    }, []);

    useEffect(() => {
        paginationModel.page >= 0 && fetchData();
    }, [paginationModel]);

    useEffect(() => {
        if (status === 204 || data?.count === 0 || data?.results?.length === 0) {
            setNoData(true);
        } else if (data?.results?.length > 0) {
            const updatedData = data.results.map(data => updateData(data, OrganizationHeaders, 'o', enums));
            setNewData(updatedData);
            setNoData(false);
            setTotalPages(Math.ceil(data.count / PAGE_SIZE));
        } else {
            setNoData(false);
        }
    }, [data, status])

    useEffect(() => {
        if (searchData.results?.length > 0) {
            setFilteredData(searchData.results.map(data => updateData(data, OrganizationHeaders, 'o', enums)));
            setTotalPages(Math.ceil(searchData.count / PAGE_SIZE));
        } else {
            setFilteredData([]);
            setTotalPages(0);
        }
    }, [searchData, searchStatus]);

    const handleColumnResize = (params) => {
        setViewHeaders((prevColumns) =>
            prevColumns.map((col) =>
                col.field === params.colDef.field
                    ? {...col, width: params.width}
                    : col
            )
        );
    };

    const handleRowClick = (param, event) => {
        event.preventDefault();
        window.history.replaceState({from: location}, '');
        navigate(`/organization/${param.row.id}`, {state: {from: location}});
    };

    const handleEntry = (event) => {
        event.preventDefault();
        navigate(`/organization/new`, {state: {from: location}});
    };

    const handleSearch = async () => {
        setFilteredData([]);
        setSearching(true);
        sessionStorage.setItem('organizationSearching', 'T')
        setPaginationModel((pm) => ({...pm, page: 0}));
        sessionStorage.setItem('organizationPage', '0');
    }

    const handleOpenSearch = () => {
        setOpenSearch(true);
        sessionStorage.setItem('organizationOpenSearch', 'T');
    }

    const handleCancelSearch = () => {
        if (searching) {
            setPaginationModel((pm) => ({...pm, page: 0}));
            sessionStorage.setItem('organizationPage', '0');
        }
        setOpenSearch(false);
        sessionStorage.setItem('organizationOpenSearch', 'F');
        setSearching(false);
        sessionStorage.setItem('organizationSearching', 'F');
        setSearchParams({});
        sessionStorage.setItem('organizationSearchParams', '{}');
        setFilteredData([]);
    }

    const handlePageSelect = (e) => {
        setPaginationModel((pm) => ({...pm, page: e.target.value}));
        sessionStorage.setItem('organizationPage', e.target.value);
    }

    const handlePaginationModelUpdate = (model, details) => {
        setPaginationModel(model);
        sessionStorage.setItem('organizationPage', model.page);
    }

    const handleSearchParams = (params) => {
        sessionStorage.setItem('organizationSearchParams', JSON.stringify(params));
        setSearchParams(params);
    }

    const handleColumnVisibilityLabel = (columns) => {
        sessionStorage.setItem('organizationColumns', JSON.stringify(columns));
        setColumnVisibilityModel(columns);
    }


    if (!webAdmin) navigate(from);

    if ((!searching && loading) || (searching && searchLoading)) return <LoadingComponent/>;
    if (noData) return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justify="center"
        >
            <Grid size={12}/>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='organization' display='Organization'/>
            </Grid>
            <Grid size={6}>
                <Typography variant='h4'>Organizations</Typography>
            </Grid>
            {webAdmin && <Grid size={6} sx={{display: "flex", justifyContent: "flex-end"}}>
                <Button component="label"
                        variant="contained"
                        onClick={handleEntry}
                        startIcon={<AddIcon/>}
                >
                    New Organization
                </Button>
            </Grid>}
            <Grid size={12}>
                <NoDataComponent/>
            </Grid>
        </Grid>
    )

    return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justifyContent="space-between"
        >
            <Grid size={12}>
            </Grid>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='organization' display='Organization'/>
            </Grid>
            <Grid size={6}>
                <Typography variant='h4'>Organizations</Typography>
            </Grid>
            {webAdmin &&
                <Grid size={6} sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button
                        component="label"
                        variant="contained"
                        onClick={handleEntry}
                        startIcon={<AddIcon/>}>
                        New Organization
                    </Button>
                </Grid>}
            <Grid size={6}/>
            <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                <Button
                    component="label"
                    onClick={openSearch ? handleCancelSearch : handleOpenSearch}
                    startIcon={<ManageSearchIcon/>}
                    sx={{mb: 1}}>
                    {openSearch ? "Cancel" : "Filters"}
                </Button>
            </Grid>
            {openSearch && <Grid size={12}>
                <SearchBox
                    prefix='c'
                    searchParams={searchParams}
                    setSearchParams={handleSearchParams}
                    searchFields={organizationSearchFields}
                    sendSearch={handleSearch}
                    sx={{margin: 'auto', mt: 2, mb: 5, maxWidth: '100%'}}
                />
            </Grid>}
            <Grid size={12}/>
            {searching && <Grid size={12}>
                <Typography variant='h6'>
                    Filtered Results:
                </Typography>
            </Grid>}
            <Grid size={12}>
                <DataGrid
                    disableColumnFilter
                    columns={viewHeaders}
                    onColumnResizeCommitted={handleColumnResize}
                    rows={searching ? filteredData : newData}
                    rowCount={(searching ? searchData.count : data.count) || 0}
                    paginationMode="server"
                    slots={{toolbar: CustomToolbar}}
                    showToolbar
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelUpdate}
                    pageSizeOptions={[PAGE_SIZE]}
                    rowHeight={80}
                    onRowClick={handleRowClick}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityLabel}
                    slotProps={{
                        columnsManagement: {
                            disableResetButton: true,
                        },
                        toolbar: {handleReset: handleReset},
                    }}
                />
            </Grid>
            {Boolean(totalPages) &&
                <Grid size={12} sx={{display: "flex", justifyContent: "flex-end", alignItems: 'center'}}>
                    <Typography sx={{mr: 1}}>Page: </Typography>
                    <Select
                        name='Page'
                        key='page'
                        onChange={handlePageSelect}
                        value={paginationModel.page}
                        sx={{
                            minWidth: 70,
                            '& .MuiSelect-select': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    >
                        {Array.from({length: totalPages}, (_, index) => (
                            <MenuItem key={index} value={index}>
                                {index + 1}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>}
        </Grid>
    )


}